<script lang="ts">
	import { onMount } from 'svelte';
	import { Card, Button, Modal, Label, Input, Select, Toggle, Checkbox, Spinner, Badge } from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import ServiceDiagnostics from '../../components/admin/ServiceDiagnostics.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import {
		listServices, getService, createService, updateService, deleteService, setServiceProbes,
		listServiceGroups, createServiceGroup, updateServiceGroup, deleteServiceGroup,
		listProbes, getProbe, getServiceMetrics, setServiceMetrics, reorderServices, reorderServiceGroups
	} from '../../AppNetwork';
	import type { AdminService, ServiceGroup, Probe, ProbeMetric, ServiceMetric, Comparison } from '../../AppTypes';
	import { statusLabel, statusText } from '../../statusUtils';

	let services: AdminService[] = [];
	let groups: ServiceGroup[] = [];
	let probes: Probe[] = [];
	let loading = true;
	let error = '';

	// Services grouped for display: each group (in order) then a trailing "Ungrouped" bucket.
	// Within a group, order follows the `services` array (which the server returns sorted by
	// displayOrder), so reordering = mutating that array.
	$: sections = [
		...groups.map((group) => ({ group, services: services.filter((s) => s.groupId === group.id) })),
		{ group: null as ServiceGroup | null, services: services.filter((s) => s.groupId === null) }
	];

	function sectionKey(group: ServiceGroup | null): string {
		return group ? `g${group.id}` : 'ungrouped';
	}

	// --- Drag and drop --------------------------------------------------------
	let dragKind: 'group' | 'service' | null = null;
	let draggedGroupId: number | null = null;
	let draggedServiceId: number | null = null;
	let overGroupKey: string | null = null;
	let overServiceId: number | null = null;

	function resetDrag() {
		dragKind = null;
		draggedGroupId = null;
		draggedServiceId = null;
		overGroupKey = null;
		overServiceId = null;
	}

	function groupDragStart(group: ServiceGroup) {
		dragKind = 'group';
		draggedGroupId = group.id;
		expandedId = null;
	}
	function serviceDragStart(service: AdminService) {
		dragKind = 'service';
		draggedServiceId = service.id;
		expandedId = null;
	}

	function headerDragOver(e: DragEvent, key: string) {
		e.preventDefault();
		overGroupKey = key;
		overServiceId = null;
	}
	function rowDragOver(e: DragEvent, id: number) {
		e.preventDefault();
		overServiceId = id;
		overGroupKey = null;
	}

	/** Drop on a group header (or its empty body): reorder groups, or move a service into the group. */
	async function headerDrop(group: ServiceGroup | null) {
		const kind = dragKind;
		const gid = draggedGroupId;
		const sid = draggedServiceId;
		resetDrag();

		if (kind === 'group' && gid !== null && group && gid !== group.id) {
			const next = [...groups];
			const [moved] = next.splice(next.findIndex((g) => g.id === gid), 1);
			// Recompute the target index AFTER removal so the group always lands
			// before the target (matching the drop line), regardless of direction.
			next.splice(next.findIndex((g) => g.id === group.id), 0, moved);
			groups = next;
			try {
				await reorderServiceGroups(groups.map((g) => g.id));
			} catch (e) {
				error = e instanceof Error ? e.message : 'Failed to save group order';
				await loadAll();
			}
		} else if (kind === 'service' && sid !== null) {
			const targetGroupId = group ? group.id : null;
			// Dropping on a header inserts at the start of the group (the line shows
			// under the header); append only if there's nothing else there.
			const firstId = services.find((s) => s.groupId === targetGroupId && s.id !== sid)?.id ?? null;
			const groupChanged = applyServiceMove(sid, targetGroupId, firstId);
			await persistServiceMove(sid, targetGroupId, groupChanged);
		}
	}

	/** Drop on a service row: insert the dragged service before it (adopting that row's group). */
	async function rowDrop(target: AdminService) {
		const kind = dragKind;
		const sid = draggedServiceId;
		resetDrag();
		if (kind !== 'service' || sid === null || sid === target.id) {
			return;
		}
		const groupChanged = applyServiceMove(sid, target.groupId, target.id);
		await persistServiceMove(sid, target.groupId, groupChanged);
	}

	/** Reorder/move within the local `services` array. Returns whether the group changed. */
	function applyServiceMove(sid: number, targetGroupId: number | null, beforeServiceId: number | null): boolean {
		const moved = services.find((s) => s.id === sid);
		if (!moved) {
			return false;
		}
		const groupChanged = moved.groupId !== targetGroupId;
		moved.groupId = targetGroupId;
		const rest = services.filter((s) => s.id !== sid);
		let insertAt: number;
		if (beforeServiceId !== null) {
			insertAt = rest.findIndex((s) => s.id === beforeServiceId);
		} else {
			// Append after the last service already in the target group.
			let last = -1;
			rest.forEach((s, idx) => {
				if (s.groupId === targetGroupId) last = idx;
			});
			insertAt = last === -1 ? rest.length : last + 1;
		}
		if (insertAt < 0) insertAt = rest.length;
		rest.splice(insertAt, 0, moved);
		services = rest;
		return groupChanged;
	}

	async function persistServiceMove(sid: number, targetGroupId: number | null, groupChanged: boolean) {
		try {
			if (groupChanged) {
				await updateService(sid, { groupId: targetGroupId });
			}
			const ids = services.filter((s) => s.groupId === targetGroupId).map((s) => s.id);
			await reorderServices(ids);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save new order';
			await loadAll();
		}
	}

	// --- Service modal --------------------------------------------------------
	let showModal = false;
	let editing: AdminService | null = null;
	let form = blankForm();
	let selectedProbeIds: number[] = [];
	let serviceMetrics: ServiceMetric[] = [];
	let probeMetricsMap: Record<number, ProbeMetric[]> = {};
	let isSaving = false;
	let expandedId: number | null = null;

	function toggleExpand(id: number) {
		expandedId = expandedId === id ? null : id;
	}

	const comparisons: Comparison[] = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'];
	const BUILTIN_METRIC_OPTIONS = [
		{ key: '$up', label: 'Availability ($up)' },
		{ key: '$responseTime', label: 'Response time ($responseTime)' }
	];

	function probeName(id: number): string {
		return probes.find((p) => p.id === id)?.name ?? `#${id}`;
	}

	function metricKeyOptions(probeId: number): { key: string; label: string }[] {
		const provided = (probeMetricsMap[probeId] ?? []).map((m) => ({ key: m.key, label: `${m.label} (${m.key})` }));
		return [...BUILTIN_METRIC_OPTIONS, ...provided];
	}

	async function ensureProbeMetrics(probeId: number) {
		if (probeMetricsMap[probeId]) return;
		try {
			const full = await getProbe(probeId);
			probeMetricsMap = { ...probeMetricsMap, [probeId]: full.metrics ?? [] };
		} catch {
			probeMetricsMap = { ...probeMetricsMap, [probeId]: [] };
		}
	}

	function blankForm() {
		return { name: '', slug: '', description: '', groupId: null as number | null, displayOrder: 0, isPublic: true };
	}

	async function loadAll() {
		loading = true;
		error = '';
		try {
			[services, groups, probes] = await Promise.all([
				listServices(),
				listServiceGroups(),
				listProbes({ limit: 200 }).then((r) => r.items)
			]);
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load services';
		} finally {
			loading = false;
		}
	}
	onMount(loadAll);

	function openCreate() {
		editing = null; form = blankForm(); selectedProbeIds = []; serviceMetrics = []; probeMetricsMap = {}; error = ''; showModal = true;
	}
	async function openEdit(service: AdminService) {
		error = '';
		const full = await getService(service.id);
		editing = full;
		form = { name: full.name, slug: full.slug, description: full.description ?? '', groupId: full.groupId, displayOrder: full.displayOrder, isPublic: full.isPublic };
		selectedProbeIds = full.probeIds ?? [];
		probeMetricsMap = {};
		await Promise.all(selectedProbeIds.map((id) => ensureProbeMetrics(id)));
		const loaded = await getServiceMetrics(service.id);
		serviceMetrics = loaded.map((m) => ({ ...m, thresholds: (m.thresholds ?? []).map((t) => ({ ...t })) }));
		showModal = true;
	}

	async function openDuplicate(service: AdminService) {
		error = '';
		const full = await getService(service.id);
		editing = null; // create a new service from these values
		form = { name: `${full.name} (copy)`, slug: `${full.slug}-copy`, description: full.description ?? '', groupId: full.groupId, displayOrder: full.displayOrder, isPublic: full.isPublic };
		selectedProbeIds = full.probeIds ?? [];
		probeMetricsMap = {};
		await Promise.all(selectedProbeIds.map((id) => ensureProbeMetrics(id)));
		const loaded = await getServiceMetrics(service.id);
		serviceMetrics = loaded.map((m) => ({ probeId: m.probeId, metricKey: m.metricKey, enabled: m.enabled, evalMode: m.evalMode ?? 'latest', evalWindow: m.evalWindow ?? 1, thresholds: (m.thresholds ?? []).map((t) => ({ comparison: t.comparison, value: t.value, severity: t.severity, enabled: t.enabled })) }));
		showModal = true;
	}

	function addServiceMetric() {
		const probeId = selectedProbeIds[0] ?? 0;
		serviceMetrics = [...serviceMetrics, { probeId, metricKey: '$up', enabled: true, evalMode: 'latest', evalWindow: 1, thresholds: [] }];
	}
	function removeServiceMetric(index: number) {
		serviceMetrics = serviceMetrics.filter((_, i) => i !== index);
	}
	function addThreshold(index: number) {
		serviceMetrics[index].thresholds = [
			...(serviceMetrics[index].thresholds ?? []),
			{ comparison: 'gt', value: '', severity: 'degraded', enabled: true }
		];
		serviceMetrics = serviceMetrics;
	}
	function removeThreshold(index: number, ti: number) {
		serviceMetrics[index].thresholds = (serviceMetrics[index].thresholds ?? []).filter((_, i) => i !== ti);
		serviceMetrics = serviceMetrics;
	}

	async function save() {
		isSaving = true;
		error = '';
		try {
			const payload = { ...form, groupId: form.groupId === null ? null : Number(form.groupId) };
			const id = editing ? (await updateService(editing.id, payload)).id : (await createService(payload)).id;
			await setServiceProbes(id, selectedProbeIds.map(Number));
			await setServiceMetrics(id, serviceMetrics.filter((m) => m.probeId && m.metricKey));
			showModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save service';
		}
		isSaving = false;
	}

	async function remove(service: AdminService) {
		if (!confirm(`Delete service "${service.name}"?`)) return;
		await deleteService(service.id);
		await loadAll();
	}

	function toggleProbe(id: number, checked: boolean) {
		selectedProbeIds = checked ? [...selectedProbeIds, id] : selectedProbeIds.filter((p) => p !== id);
		if (checked) {
			void ensureProbeMetrics(id);
		}
	}

	// --- Group modal ----------------------------------------------------------
	let showGroupModal = false;
	let editingGroup: ServiceGroup | null = null;
	let groupForm = blankGroupForm();
	let groupSaving = false;

	function blankGroupForm() {
		return { name: '', slug: '', description: '', displayOrder: 0, isPublic: true };
	}
	function openCreateGroup() {
		editingGroup = null; groupForm = blankGroupForm(); error = ''; showGroupModal = true;
	}
	function openEditGroup(group: ServiceGroup) {
		editingGroup = group; error = '';
		groupForm = { name: group.name, slug: group.slug, description: group.description ?? '', displayOrder: group.displayOrder, isPublic: group.isPublic };
		showGroupModal = true;
	}
	async function saveGroup() {
		groupSaving = true;
		error = '';
		try {
			if (editingGroup) await updateServiceGroup(editingGroup.id, groupForm);
			else await createServiceGroup(groupForm);
			showGroupModal = false;
			await loadAll();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to save group';
		}
		groupSaving = false;
	}
	async function removeGroup(group: ServiceGroup) {
		if (!confirm(`Delete group "${group.name}"? Services in it become ungrouped.`)) return;
		await deleteServiceGroup(group.id);
		await loadAll();
	}
</script>

<PageHeader title="Services" description="Products shown on the public status page, organized into groups. Drag the grips to reorder, or drop a service onto another group to move it." />

<div class="space-y-6">
	<Card class="max-w-none">
		<div class="flex flex-wrap items-center justify-end gap-2">
			<Button color="alternative" on:click={openCreateGroup}><i class="fa-solid fa-layer-group me-2"></i>New Group</Button>
			<Button on:click={openCreate}><i class="fa-solid fa-plus me-2"></i>New Service</Button>
		</div>
	</Card>

	{#if loading}
		<Card class="max-w-none"><div class="p-4 text-center text-gray-500 dark:text-gray-400"><i class="fa-solid fa-spinner fa-spin text-2xl mb-2"></i><p>Loading services…</p></div></Card>
	{:else if error}
		<Card class="max-w-none"><div class="p-4 text-center"><i class="fa-solid fa-exclamation-triangle text-3xl text-red-500 mb-2"></i><p class="text-gray-500 dark:text-gray-400">{error}</p><Button class="mt-3" on:click={loadAll}>Retry</Button></div></Card>
	{:else if services.length === 0 && groups.length === 0}
		<Card class="max-w-none"><div class="p-8 text-center"><i class="fa-solid fa-server text-4xl text-gray-300 dark:text-gray-600 mb-4"></i><p class="text-gray-500 dark:text-gray-400">No services or groups yet</p></div></Card>
	{:else}
		<div class="space-y-4">
			{#each sections as section (sectionKey(section.group))}
				{#if section.group || section.services.length > 0}
					<div class="relative rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
						<!-- Drop line: a group will be placed before this one -->
						{#if dragKind === 'group' && section.group && overGroupKey === sectionKey(section.group)}
							<div class="absolute left-0 right-0 top-0 h-0.5 bg-primary-500 z-20 pointer-events-none"></div>
						{/if}
						<!-- Group header / drop-into target -->
						<div
							class="group flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700"
							on:dragover={(e) => headerDragOver(e, sectionKey(section.group))}
							on:drop|preventDefault={() => headerDrop(section.group)}
						>
							{#if section.group}
								<span
									class="cursor-move text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
									draggable="true" role="button" tabindex="-1" aria-label="Drag to reorder group" title="Drag to reorder group"
									on:dragstart={() => groupDragStart(section.group)}
									on:dragend={resetDrag}
								><i class="fa-solid fa-grip-vertical"></i></span>
								<span class="font-semibold text-gray-900 dark:text-white">{section.group.name}</span>
								<span class="text-xs text-gray-400 font-mono">{section.group.slug}</span>
								{#if !section.group.isPublic}<Badge color="dark">hidden</Badge>{/if}
								<div class="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
									<RowAction icon="fa-pen" label="Edit group" on:click={() => openEditGroup(section.group)} />
									<RowAction icon="fa-trash" label="Delete group" danger on:click={() => removeGroup(section.group)} />
								</div>
							{:else}
								<span class="font-semibold text-gray-500 dark:text-gray-400">Ungrouped</span>
							{/if}
						</div>

						<!-- Services in this group -->
						<div class="relative divide-y divide-gray-100 dark:divide-gray-700">
							<!-- Drop line: a service will be placed at the start of this group -->
							{#if dragKind === 'service' && overGroupKey === sectionKey(section.group)}
								<div class="absolute left-0 right-0 top-0 h-0.5 bg-primary-500 z-20 pointer-events-none"></div>
							{/if}
							{#each section.services as service (service.id)}
								<div
									class="group relative flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 {draggedServiceId === service.id ? 'opacity-40' : ''}"
									on:dragover={(e) => rowDragOver(e, service.id)}
									on:drop|preventDefault={() => rowDrop(service)}
								>
									{#if dragKind === 'service' && overServiceId === service.id}
										<div class="absolute left-0 right-0 -top-px h-0.5 bg-primary-500 z-20 pointer-events-none"></div>
									{/if}
									<span
										class="cursor-move text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
										draggable="true" role="button" tabindex="-1" aria-label="Drag to reorder / move service" title="Drag to reorder or move to another group"
										on:dragstart={() => serviceDragStart(service)}
										on:dragend={resetDrag}
									><i class="fa-solid fa-grip-vertical"></i></span>
									<button type="button" class="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1" aria-label="Toggle details" on:click={() => toggleExpand(service.id)}>
										<i class="fa-solid fa-chevron-right transition-transform {expandedId === service.id ? 'rotate-90' : ''}"></i>
									</button>
									<span class="font-medium text-gray-900 dark:text-white">{service.name}</span>
									<span class="text-sm {statusText(service.currentStatus)}">{statusLabel(service.currentStatus)}</span>
									{#if !service.isPublic}<Badge color="dark">hidden</Badge>{/if}
									<div class="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
										<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(service)} />
										<RowAction icon="fa-copy" label="Duplicate" on:click={() => openDuplicate(service)} />
										<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(service)} />
									</div>
								</div>
								{#if expandedId === service.id}
									<ServiceDiagnostics serviceId={service.id} />
								{/if}
							{/each}

							{#if section.services.length === 0}
								<div
									class="px-3 py-4 text-xs text-gray-400 text-center"
									on:dragover={(e) => headerDragOver(e, sectionKey(section.group))}
									on:drop|preventDefault={() => headerDrop(section.group)}
								>
									No services{section.group ? ' in this group' : ''} — drag one here.
								</div>
							{/if}
						</div>
					</div>
				{/if}
			{/each}
		</div>
	{/if}
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Service' : 'New Service'} size="lg">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={form.name} /></div>
		<div><Label class="mb-1">Slug</Label><Input bind:value={form.slug} placeholder="web-app" /></div>
		<div><Label class="mb-1">Description</Label><Input bind:value={form.description} /></div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Group</Label>
				<Select bind:value={form.groupId}>
					<option value={null}>— None —</option>
					{#each groups as g}<option value={g.id}>{g.name}</option>{/each}
				</Select>
			</div>
			<div><Label class="mb-1">Display order</Label><Input type="number" bind:value={form.displayOrder} /></div>
		</div>
		<Toggle bind:checked={form.isPublic}>Public</Toggle>

		<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
			<Label class="mb-1">Probes</Label>
			<div class="mt-2 space-y-1 max-h-48 overflow-auto">
				{#each probes as probe}
					<Checkbox checked={selectedProbeIds.includes(probe.id)} on:change={(e) => toggleProbe(probe.id, e.currentTarget.checked)}>
						{probe.name} <span class="text-gray-400 text-xs ml-1">{probe.targetUrl}</span>
					</Checkbox>
				{/each}
				{#if probes.length === 0}<p class="text-xs text-gray-400">No probes — create probes first.</p>{/if}
			</div>
		</div>

		<div class="border-t border-gray-200 dark:border-gray-700 pt-4">
			<div class="flex items-center justify-between mb-1">
				<Label>Status metrics &amp; thresholds</Label>
				<Button size="xs" color="alternative" on:click={addServiceMetric} disabled={selectedProbeIds.length === 0}>
					<i class="fa-solid fa-plus me-1"></i>Add metric
				</Button>
			</div>
			<p class="text-xs text-gray-400 mb-3">This service's status (and incidents) come from these metrics' thresholds. Add one or more.</p>

			{#each serviceMetrics as sm, mi}
				<div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
					<div class="grid grid-cols-12 gap-2 items-center mb-2">
						<Select class="col-span-4" size="sm" bind:value={sm.probeId}>
							{#each selectedProbeIds as pid}<option value={pid}>{probeName(pid)}</option>{/each}
						</Select>
						<Select class="col-span-5" size="sm" bind:value={sm.metricKey}>
							{#each metricKeyOptions(sm.probeId) as opt}<option value={opt.key}>{opt.label}</option>{/each}
						</Select>
						<div class="col-span-2"><Toggle bind:checked={sm.enabled}>On</Toggle></div>
						<Button class="col-span-1" size="xs" color="red" on:click={() => removeServiceMetric(mi)}><i class="fa-solid fa-trash"></i></Button>
					</div>
					<div class="flex flex-wrap items-center gap-2 mb-2 text-xs text-gray-500 dark:text-gray-400">
						<span>Evaluate</span>
						<Select size="sm" class="!w-auto" bind:value={sm.evalMode}>
							<option value="latest">latest value</option>
							<option value="average">average of N</option>
							<option value="consistent">N consecutive breaches</option>
						</Select>
						{#if sm.evalMode && sm.evalMode !== 'latest'}
							<span>over the last</span>
							<Input size="sm" type="number" min="1" class="!w-20" bind:value={sm.evalWindow} />
							<span>{sm.evalMode === 'average' ? 'samples (mean vs thresholds)' : 'runs (all must breach)'}</span>
						{/if}
					</div>
					<div class="pl-2 border-l-2 border-gray-100 dark:border-gray-700">
						{#each sm.thresholds ?? [] as threshold, ti}
							<div class="grid grid-cols-12 gap-2 items-center mb-2">
								<span class="col-span-2 text-xs text-gray-400">when value</span>
								<Select class="col-span-2" size="sm" bind:value={threshold.comparison}>
									{#each comparisons as c}<option value={c}>{c}</option>{/each}
								</Select>
								<Input class="col-span-2" size="sm" type="text" placeholder="value" bind:value={threshold.value} />
								<span class="col-span-1 text-xs text-gray-400">→</span>
								<Select class="col-span-3" size="sm" bind:value={threshold.severity}>
									<option value="degraded">Degraded</option>
									<option value="major_outage">Outage</option>
								</Select>
								<Button class="col-span-2" size="xs" color="red" on:click={() => removeThreshold(mi, ti)}>Remove</Button>
							</div>
						{/each}
						<Button size="xs" color="alternative" on:click={() => addThreshold(mi)}><i class="fa-solid fa-plus me-1"></i>Add threshold</Button>
						{#if (sm.thresholds ?? []).length === 0}<span class="text-xs text-gray-400 ml-2">No thresholds — this metric won't affect status.</span>{/if}
					</div>
				</div>
			{/each}
			{#if serviceMetrics.length === 0}<p class="text-xs text-gray-400">No status metrics — this service stays "unknown". Add e.g. Availability ($up) eq 0 → Outage.</p>{/if}
		</div>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>

<Modal bind:open={showGroupModal} title={editingGroup ? 'Edit Group' : 'New Group'} size="md">
	<div class="space-y-4">
		<div><Label class="mb-1">Name</Label><Input bind:value={groupForm.name} placeholder="Core Services" /></div>
		<div><Label class="mb-1">Slug</Label><Input bind:value={groupForm.slug} placeholder="core" /></div>
		<div><Label class="mb-1">Description</Label><Input bind:value={groupForm.description} /></div>
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Display order</Label><Input type="number" bind:value={groupForm.displayOrder} /></div>
			<div class="flex items-end"><Toggle bind:checked={groupForm.isPublic}>Public</Toggle></div>
		</div>
		{#if error}<p class="text-red-500 text-sm">{error}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={saveGroup} disabled={groupSaving || !groupForm.name || !groupForm.slug}>
				{#if groupSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showGroupModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
